import { useLayoutEffect } from 'react';

/**
 * Puts `site-look` on <html> for the audiences in SITE_LOOK_ROLES, and takes it off for everyone else.
 *
 * ── WHERE THIS MUST BE CALLED ────────────────────────────────────────────────────────────────────────────────
 * In AppRoutes, ABOVE the early returns for /terms, /privacy and /share/redemption/* (App.js). Those return
 * before the <Routes> table, so a hook placed below them runs on some paths and not others, and React fails with
 * "rendered fewer hooks than expected" the moment the path changes. **A new early return goes BELOW this call.**
 *
 * THE BUILD ENFORCES THIS, shown by a control on 2026-09-28: with the call moved below the early returns, `yarn
 * build` fails on react-hooks/rules-of-hooks ("Did you accidentally call a React Hook after an early return?").
 * That only works because this is a NAMED HOOK whose name starts with `use`. Inline the three lines back into
 * AppRoutes as a plain call and the lint can no longer see it; keep it a hook.
 *
 * It lived in Layout until 2026-09-28, which is why /order-complete -- the page a client lands on straight after
 * paying -- never got the look: Layout is not mounted there. At app level it covers every route, including the
 * ones outside Layout (/order-complete, the * catch-all, /terms, /privacy, the share page, /verify-email,
 * /invite/:token). See KNOWN-ISSUES 0bd for the enumerated list.
 *
 * ── WHY IT IS PURELY ROLE-DRIVEN, WITH NO PATH LIST ──────────────────────────────────────────────────────────
 * Because the class is inert by construction anywhere the look has not been fitted, not because of which pages
 * happen to force dark. site-look.css carries 191 rules under `html.site-look` that name no marker: four declare
 * only custom properties (--sl-page, --sl-teal, ...), and a custom property paints nothing without a consumer;
 * the other 187 are scoped to chrome that only Layout renders (main-content, sidebar, board-column,
 * gem-tier-widget, pixally-ad, email-verify-banner, the brand-bar pills and menus). Every other consumer sits
 * behind .site-look-page, .ug-modal, project-panel or fullscreen-overlay.
 *
 * Exactly four rules have no element hook at all. Three are .light-scoped (the scrollbar pair and the light
 * ground, site-look.css:136, 137, 1214) and cannot fire on the public pages, which call useForceDark. The fourth
 * is `html.site-look ::selection` (135), which does apply: a signed-in client selecting text on /terms gets a
 * teal highlight. That is the whole visible consequence, and it is the brand colour.
 *
 * If a public page is ever added that does NOT force dark, require a marker on THAT RULE. Do not start keeping a
 * list of paths here: a path list is the rot-prone construct that was removed at the dialog level (0az, 0bd).
 *
 * useForceDark is independent: it only adds and removes .light/.dark through applyTheme, never this class.
 *
 * A LAYOUT effect, not a passive one, so the class is on before the first paint and the old look never flashes.
 */
/**
 * The audiences the look is switched on for. A SET, not a boolean, so the migration can move one audience at a
 * time: the whole portal converts in slices, and an audience that is mid-conversion can be rolled back by taking
 * it out of here without touching the others.
 *
 * Adding a role turns on the CHROME, the BOARD and the DRAWER for it immediately, because those are keyed on
 * testids every role already renders (sidebar, board-column with 94 ungated rules, project-panel with 25) rather
 * than on a page marker. Their own pages stay in the portal's look until each gets `.site-look-page`. That gap is
 * the reason this is a set: an audience sits in new chrome over old page interiors for exactly as long as its
 * pass takes, and nobody else has to.
 *
 * 2026-09-28: client (shipped), admin and staff (converting). EDITOR was held back for the length of the admin pass.
 * 2026-09-29: editor joins (Ryan: "start the editor side"), which turns on the chrome, board and drawer for editors;
 * their own pages convert one commit each after this.
 * When every audience is converted, collapse this to `true` and delete the set.
 */
export const SITE_LOOK_ROLES = new Set(['client', 'admin', 'staff', 'editor']);

export function useSiteLook(role) {
  useLayoutEffect(() => {
    const html = document.documentElement;
    const on = SITE_LOOK_ROLES.has(role);
    html.classList.toggle('site-look', on);
    // The viewer's role, for the few rules that differ by audience. First: the client chat is ORANGE for staff (to
    // tell it from the editor chat beside it) and teal for a client, who has only the one thread (Ryan, 2026-09-29).
    if (on) html.dataset.slRole = role; else delete html.dataset.slRole;
    return () => { html.classList.remove('site-look'); delete html.dataset.slRole; };
  }, [role]);
}
